/**
 * The traffic's lamps: headlights, tail lights and brake lights.
 *
 * - **Headlights** burn by day too, dimly: daytime running lights, as the law
 *   has every car in Sweden drive with. At dusk they come up full.
 * - **Tail lights** come on with the headlights; **brake lights**, brighter,
 *   whenever a car slows hard or stands, by day as by night.
 *
 * Only the lamps glow: no light is thrown on the road. A real light per car,
 * seventy of them, would cost more than the whole rest of the scene; the
 * player's own car has real ones (`Vehicle.headlights`).
 *
 * Lamps are instanced boxes in an unlit material, each car's placed in its
 * own frame; all in a handful of draw calls. They stand where the model's own
 * lamps were (`takeLamps`), which are left out of the baked body, so each lamp
 * is drawn once; a body with none found falls back to its bounds.
 */

import {
  Box3, BoxGeometry, Color, DynamicDrawUsage, Group, InstancedMesh, Matrix4, Mesh, MeshBasicMaterial, Vector3,
  type BufferGeometry, type Material, type Object3D,
} from 'three';

/** One lamp on a body, in its frame: its middle, and its size, metres. */
export interface LampSpot {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}

/**
 * The lamps of a model about to be baked: each mesh in `heads` or `tails`
 * material, where it sits relative to `root` and how big it is. Those meshes
 * are hidden, so the bake leaves them out and the traffic's own lamps stand
 * in their place, drawn once.
 */
export function takeLamps(root: Object3D, heads: readonly Material[], tails: readonly Material[]): { heads: LampSpot[]; tails: LampSpot[] } {
  root.updateMatrixWorld(true);
  const toRoot = new Matrix4().copy(root.matrixWorld).invert();
  const out = { heads: [] as LampSpot[], tails: [] as LampSpot[] };
  const found: Mesh[] = [];
  root.traverseVisible((object) => {
    if (!(object instanceof Mesh)) return;
    const material = object.material as Material;
    const list = heads.includes(material) ? out.heads : tails.includes(material) ? out.tails : undefined;
    if (list === undefined) return;
    object.geometry.computeBoundingBox();
    const box = new Box3().copy(object.geometry.boundingBox!).applyMatrix4(new Matrix4().multiplyMatrices(toRoot, object.matrixWorld));
    const centre = box.getCenter(new Vector3()), size = box.getSize(new Vector3());
    list.push({ x: centre.x, y: centre.y, z: centre.z, w: size.x, h: size.y, d: size.z });
    found.push(object);
  });
  for (const mesh of found) mesh.visible = false;
  return out;
}

/** Where a body's lamps go, from its bounds: x right, y up, z forward-negative. */
export interface LampLayout {
  /** Half the body's width, metres. */
  halfWidth: number;
  /** Its front (most negative z) and back, metres. */
  front: number;
  back: number;
  /** How high the lamps are. */
  height: number;
  /** One headlight in the middle: a motorbike. */
  single: boolean;
  /** The model's own lamps, where it had them (`takeLamps`): drawn there in place of the guess from the bounds. */
  heads?: readonly LampSpot[];
  tails?: readonly LampSpot[];
}

/** A body's lamp layout: its own lamps (`takeLamps`), or a guess from its geometry's bounding box. */
export function lampLayout(body: BufferGeometry, single = false, lamps?: { heads: LampSpot[]; tails: LampSpot[] }): LampLayout {
  body.computeBoundingBox();
  const b = body.boundingBox!;
  const height = b.max.y - b.min.y;
  return {
    halfWidth: (b.max.x - b.min.x) / 2, front: b.min.z, back: b.max.z, height: b.min.y + Math.min(0.75, Math.max(0.45, height * 0.42)), single,
    ...(lamps !== undefined && lamps.heads.length > 0 ? { heads: lamps.heads } : {}),
    ...(lamps !== undefined && lamps.tails.length > 0 ? { tails: lamps.tails } : {}),
  };
}

/**
 * Linear colours, unlit and not tone mapped, so a lamp at night reads as a
 * light; by day near the lenses' own colours, which they stand in for.
 */
const HEAD_DAY = new Color(0.82, 0.8, 0.72);
const HEAD_NIGHT = new Color(2.2, 2.0, 1.6);
const TAIL_OFF = new Color(0.3, 0.012, 0.01);
const TAIL_ON = new Color(0.45, 0.003, 0.002);
const BRAKE = new Color(1, 0.01, 0.005);
/** Slowing harder than this, m/s², or standing, its brake lights are on. */
const BRAKING = 0.9;

const lampGeometry = new BoxGeometry(0.3, 0.14, 0.06);
const lampMaterial = new MeshBasicMaterial({ color: 0xffffff, toneMapped: false });


const lamp = new Matrix4();
const lampSize = new Matrix4();
/** The lamp box's own size (`lampGeometry`), to scale it to a model's lamp. */
const BOX = { w: 0.3, h: 0.14, d: 0.06 };

/** `spot`, a hair larger than the model's lamp so it reads as the lamp, in `body`'s frame. */
function spotMatrix(spot: LampSpot, body: Matrix4): Matrix4 {
  lampSize.makeScale((spot.w + 0.006) / BOX.w, (spot.h + 0.006) / BOX.h, Math.max(0.012, spot.d + 0.006) / BOX.d);
  return lamp.makeTranslation(spot.x, spot.y, spot.z).multiply(lampSize).premultiply(body);
}

export class CarLights {
  readonly group = new Group();
  private readonly heads: InstancedMesh;
  private readonly tails: InstancedMesh;
  private head = 0;
  private tail = 0;
  private night = 0;
  /** Each car's speed last frame, by id: for its brake lights. */
  private readonly lastSpeed = new Map<number, number>();
  private readonly seen = new Set<number>();

  constructor(capacity: number) {
    // A model's own lamps can be more than two a side: a lorry's, a high brake light.
    this.heads = new InstancedMesh(lampGeometry, lampMaterial, capacity * 4);
    this.tails = new InstancedMesh(lampGeometry, lampMaterial, capacity * 8);
    for (const mesh of [this.heads, this.tails]) {
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.count = 0;
      this.group.add(mesh);
    }
    for (const mesh of [this.heads, this.tails]) {
      mesh.setColorAt(0, TAIL_OFF);
      mesh.instanceColor!.setUsage(DynamicDrawUsage);
    }
  }

  /** Start a frame: `night` 0 by day .. 1 at night, as the street lamps have it. */
  begin(night: number): void {
    this.head = this.tail = 0;
    this.night = night;
    this.seen.clear();
  }

  /**
   * One car's lamps, drawn at `body` (its world matrix) with `layout`; `id`
   * and `speed` (m/s) over `dt` for its brake lights. `rear` places its tail lights on another
   * body (a truck's trailer) instead, with that body's layout.
   */
  place(body: Matrix4, layout: LampLayout, id: number, speed: number, dt: number,
    rear?: { body: Matrix4; layout: LampLayout }): void {
    const last = this.lastSpeed.get(id) ?? speed;
    this.lastSpeed.set(id, speed);
    this.seen.add(id);
    const braking = speed < 0.3 || (dt > 0 && (last - speed) / dt > BRAKING);
    const headColour = this.night > 0.15 ? HEAD_NIGHT : HEAD_DAY;
    if (layout.heads !== undefined) {
      for (const spot of layout.heads) {
        if (this.head >= this.heads.instanceMatrix.count) break;
        this.heads.setMatrixAt(this.head, spotMatrix(spot, body));
        this.heads.setColorAt(this.head++, headColour);
      }
    } else {
      for (const side of layout.single ? [0] : [-1, 1]) {
        lamp.makeTranslation(side * (layout.halfWidth - 0.28), layout.height, layout.front - 0.01).premultiply(body);
        this.heads.setMatrixAt(this.head, lamp);
        this.heads.setColorAt(this.head++, headColour);
      }
    }
    const r = rear ?? { body, layout };
    const tailColour = braking ? BRAKE : this.night > 0.15 ? TAIL_ON : TAIL_OFF;
    if (r.layout.tails !== undefined) {
      for (const spot of r.layout.tails) {
        if (this.tail >= this.tails.instanceMatrix.count) break;
        this.tails.setMatrixAt(this.tail, spotMatrix(spot, r.body));
        this.tails.setColorAt(this.tail++, tailColour);
      }
    } else {
      for (const side of r.layout.single ? [0] : [-1, 1]) {
        lamp.makeTranslation(side * (r.layout.halfWidth - 0.22), r.layout.height, r.layout.back + 0.01).premultiply(r.body);
        this.tails.setMatrixAt(this.tail, lamp);
        this.tails.setColorAt(this.tail++, tailColour);
      }
    }
  }

  /** Done placing: the lamps drawn, and cars gone forgotten. */
  commit(): void {
    this.heads.count = this.head;
    this.tails.count = this.tail;
    for (const mesh of [this.heads, this.tails]) mesh.instanceMatrix.needsUpdate = true;
    this.heads.instanceColor!.needsUpdate = true;
    this.tails.instanceColor!.needsUpdate = true;
    for (const id of this.lastSpeed.keys()) if (!this.seen.has(id)) this.lastSpeed.delete(id);
  }

  addTo(parent: Object3D): void {
    parent.add(this.group);
  }
}
